import { useId, useState } from "react";
import { ConfirmDialog } from "../Modal";
import { Field, Textarea } from "../Field";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import s from "./ReasonDialog.module.scss";

/** From this many letters left, the live status says so once (and again at the limit). */
export const REASON_FEW_LEFT = 50;

type Props = {
  open: boolean;
  title: string;
  /** The text box's label ("Reason for rejecting"). */
  label: string;
  /** What leaving it empty means ("If you leave it empty, the reporter isn't notified."). */
  hint: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Said by the polite live status: `fewLeft(REASON_FEW_LEFT)` once the box gets near the limit, `atLimit` when full. */
  counterLabels: { fewLeft: (n: number) => string; atLimit: string };
  /** The word after the label that says it may stay empty ("(optional)"). */
  optionalLabel?: string;
  placeholder?: string;
  maxLength?: number;
  /** A red confirm button (reject, remove). Default true. */
  danger?: boolean;
  busy?: boolean;
  /** The reason, trimmed; "" when none was written. */
  onConfirm: (reason: string) => void;
  onClose: () => void;
};

/** A ConfirmDialog with an optional reason: a text box with a letter counter. Cancel is focused on open. */
export const ReasonDialog = ({ open, title, label, hint, confirmLabel, cancelLabel, counterLabels, optionalLabel, placeholder, maxLength = 500, danger = true, busy, onConfirm, onClose }: Props) => {
  const countId = `${useId()}-count`;
  const [text, setText] = useState("");
  // The text stays while the dialog fades out (cleared once it has gone, onExited). Reopened before the fade ends,
  // it still starts empty: reset on the closed → open edge, while rendering (as ConfirmDialog does).
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) { setWasOpen(open); if (open) setText(""); }
  const left = maxLength - text.length;
  // Words change only when a threshold is crossed, so the live region speaks once per crossing, not per letter.
  const status = left <= 0 ? counterLabels.atLimit : left <= REASON_FEW_LEFT ? counterLabels.fewLeft(REASON_FEW_LEFT) : "";
  const body = (
    <div className={s.box}>
      <Field label={label} hint={hint} optional={optionalLabel}>
        {(p) => (
          <Textarea id={p.id} rows={3} value={text} maxLength={maxLength} placeholder={placeholder} aria-describedby={[p.describedBy, countId].filter(Boolean).join(" ")}
            onChange={(e) => setText(e.target.value.slice(0, maxLength))} disabled={busy} />
        )}
      </Field>
      <span id={countId} className={s.count} dir="ltr">{`${text.length}/${maxLength}`}</span>
      <span role="status" aria-live="polite" className={VISUALLY_HIDDEN}>{status}</span>
    </div>
  );
  return (
    <ConfirmDialog open={open} title={title} body={body} confirmLabel={confirmLabel} cancelLabel={cancelLabel} danger={danger} busy={busy}
      onConfirm={() => onConfirm(text.trim())} onCancel={onClose} onExited={() => setText("")} />
  );
};
